import React, { useState, useEffect, useRef } from 'react';
import { useLang } from '../i18n';
import './LanguageSwitcher.css';

// Inline SVG flags — emoji flags don't render on Windows, so real vectors it is.
const FLAGS = {
  en: (
    <React.Fragment>
      <rect width="60" height="42" fill="#012169" />
      <path d="M0,0 60,42 M60,0 0,42" stroke="#fff" strokeWidth="8" />
      <path d="M0,0 60,42 M60,0 0,42" stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0 V42 M0,21 H60" stroke="#fff" strokeWidth="13" />
      <path d="M30,0 V42 M0,21 H60" stroke="#C8102E" strokeWidth="8" />
    </React.Fragment>
  ),
  sk: (
    <React.Fragment>
      <rect width="60" height="14" fill="#fff" />
      <rect y="14" width="60" height="14" fill="#0B4EA2" />
      <rect y="28" width="60" height="14" fill="#EE1C25" />
      <path d="M14 8 h14 v13 c0 6 -4 9.5 -7 11 c-3 -1.5 -7 -5 -7 -11 z" fill="#EE1C25" stroke="#fff" strokeWidth="1.6" />
      <path d="M20 12 h2 v3.5 h3.5 v2 h-3.5 v3 h3 v2 h-3 v4 h-2 v-4 h-3 v-2 h3 v-3 h-3.5 v-2 h3.5 z" fill="#fff" />
    </React.Fragment>
  ),
  cz: (
    <React.Fragment>
      <rect width="60" height="21" fill="#fff" />
      <rect y="21" width="60" height="21" fill="#D7141A" />
      <path d="M0,0 L30,21 L0,42 z" fill="#11457E" />
    </React.Fragment>
  ),
  pl: (
    <React.Fragment>
      <rect width="60" height="21" fill="#fff" />
      <rect y="21" width="60" height="21" fill="#DC143C" />
    </React.Fragment>
  ),
  hu: (
    <React.Fragment>
      <rect width="60" height="14" fill="#CE2939" />
      <rect y="14" width="60" height="14" fill="#fff" />
      <rect y="28" width="60" height="14" fill="#477050" />
    </React.Fragment>
  ),
  de: (
    <React.Fragment>
      <rect width="60" height="14" fill="#000" />
      <rect y="14" width="60" height="14" fill="#DD0000" />
      <rect y="28" width="60" height="14" fill="#FFCE00" />
    </React.Fragment>
  ),
  uk: (
    <React.Fragment>
      <rect width="60" height="21" fill="#005BBB" />
      <rect y="21" width="60" height="21" fill="#FFD500" />
    </React.Fragment>
  ),
};

const LANG_NAMES = {
  en: 'English',
  sk: 'Slovenčina',
  cz: 'Čeština',
  pl: 'Polski',
  hu: 'Magyar',
  de: 'Deutsch',
  uk: 'Українська',
};

// Languages whose translations were machine-drafted (small disclosure badge in the panel)
const AI_LANGS = ['sk', 'cz', 'pl', 'hu', 'de', 'uk'];

const PANEL_TITLES = {
  en: 'Select your language',
  sk: 'Vyberte jazyk',
  cz: 'Vyberte jazyk',
  pl: 'Wybierz język',
  hu: 'Válasszon nyelvet',
  de: 'Sprache auswählen',
  uk: 'Виберіть мову',
};

function Flag({ id, className }) {
  return <svg className={className} viewBox="0 0 60 42" aria-hidden="true">{FLAGS[id]}</svg>;
}

// booking.com-style selector: navbar shows the current flag; clicking opens a
// panel with all languages by native name, the active one highlighted.
function LanguageSwitcher({ size }) {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const pick = (id) => { setLang(id); setOpen(false); };

  return (
    <div className={`lang-switcher ${size === 'large' ? 'lang-switcher--large' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className="lang-btn"
        aria-label={PANEL_TITLES[lang]}
        aria-expanded={open}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((o) => !o)}
      >
        <Flag id={lang} className="lang-btn-flag" />
        <span className="lang-btn-chevron">▾</span>
      </button>

      {open && (
        <div className="lang-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="lang-panel" role="dialog" aria-label={PANEL_TITLES[lang]}>
            <div className="lang-panel-head">
              <span>{PANEL_TITLES[lang]}</span>
              <button type="button" className="lang-panel-close" aria-label="Close" onClick={() => setOpen(false)}>×</button>
            </div>
            <div className="lang-grid">
              {Object.keys(LANG_NAMES).map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`lang-opt ${lang === id ? 'active' : ''}`}
                  onClick={() => pick(id)}
                >
                  <Flag id={id} className="lang-opt-flag" />
                  <span className="lang-opt-name">
                    {LANG_NAMES[id]}
                    {AI_LANGS.includes(id) && <span className="lang-opt-ai">AI translated</span>}
                  </span>
                  {lang === id && <span className="lang-opt-check">✓</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LanguageSwitcher;
